<template>
  <div class="monthly-summary">
    <div class="page-header">
      <h2>月度汇总</h2>
    </div>

    <!-- 筛选表单 -->
    <a-card class="filter-card">
      <a-form
        :model="searchForm"
        layout="inline"
        @finish="handleSearch"
        class="search-form"
      >
        <a-form-item label="月份范围" name="monthRange">
          <a-range-picker
            v-model:value="searchForm.monthRange"
            picker="month"
            format="YYYY-MM"
            value-format="YYYYMM"
            style="width: 240px"
            :disabled-date="disabledDate"
            placeholder="['开始月份', '结束月份']"
          />
        </a-form-item>

        <a-form-item label="选择分类" name="categoryId">
          <a-tree-select
            v-model:value="searchForm.categoryId"
            :tree-data="categoryOptions"
            :field-names="{ label: 'name', value: 'id', children: 'children' }"
            placeholder="请选择商品分类"
            style="width: 200px"
            allow-clear
          />
        </a-form-item>

        <a-form-item label="商品" name="productId">
          <a-select
            v-model:value="searchForm.productId"
            show-search
            placeholder="请选择商品（可搜索）"
            style="min-width: 240px; max-width: 360px;"
            :options="productOptions"
            :loading="productOptionsLoading"
            :filter-option="filterProductOption"
            option-filter-prop="label"
            allow-clear
          />
        </a-form-item>

        <a-form-item>
          <a-button type="primary" html-type="submit" :loading="loading">
            <template #icon>
              <SearchOutlined />
            </template>
            查询
          </a-button>
          <a-button style="margin-left: 8px" @click="handleReset">
            重置
          </a-button>
        </a-form-item>
      </a-form>
    </a-card>

    <!-- 销售/退货 柱状图 -->
    <a-card v-if="summaryList && summaryList.length > 0" class="chart-card" :loading="loading">
      <template #title>销售出库 vs 退货入库（按月）</template>
      <SalesReturnBarChart :data="summaryList" x-key="month" :x-formatter="formatMonth" />
    </a-card>

    <!-- 数据表格 -->
    <a-card>
      <a-table
        :columns="columns"
        :data-source="summaryList"
        :loading="loading"
        :pagination="false"
        :scroll="{ x: 1600 }"
        :sticky="true"
        size="middle"
        bordered
        row-key="month"
      >
        <template #bodyCell="{ column, record }">
          <!-- 月份 -->
          <template v-if="column.key === 'month'">
            <span style="font-weight: 500; font-size: 14px;">{{ record.month }}</span>
          </template>

          <!-- 入库相关 -->
          <template v-else-if="column.key === 'totalInStock'">
            <span style="color: #52c41a; font-weight: 500;">{{ record.totalInStock || 0 }}</span>
          </template>

          <template v-else-if="column.key === 'inStockDetail'">
            <div style="font-size: 12px; line-height: 1.6;">
              <div>生产: <span style="color: #52c41a; font-weight: 500;">{{ record.productionStock || 0 }}</span></div>
              <div>退货: <span style="color: #fa8c16; font-weight: 500;">{{ record.returnStock || 0 }}</span></div>
              <div>其他: <span style="color: #1890ff; font-weight: 500;">{{ record.otherInStock || 0 }}</span></div>
            </div>
          </template>

          <!-- 出库相关 -->
          <template v-else-if="column.key === 'totalOutStock'">
            <span style="color: #ff4d4f; font-weight: 500;">{{ record.totalOutStock || 0 }}</span>
          </template>

          <template v-else-if="column.key === 'outStockDetail'">
            <div style="font-size: 12px; line-height: 1.6;">
              <div>销售: <span style="color: #ff4d4f; font-weight: 500;">{{ record.salesStock || 0 }}</span></div>
              <div>其他: <span style="color: #722ed1; font-weight: 500;">{{ record.otherOutStock || 0 }}</span></div>
            </div>
          </template>

          <!-- 净销售 -->
          <template v-else-if="column.key === 'netSalesStock'">
            <span :style="{ color: getNetSalesColor(record.netSalesStock), fontWeight: 500 }">
              {{ record.netSalesStock || 0 }}
            </span>
          </template>

          <!-- 退货率 -->
          <template v-else-if="column.key === 'returnRate'">
            <a-tag :color="getReturnRateColor(record.returnRate)" style="font-weight: 500;">
              {{ record.returnRate || '0%' }}
            </a-tag>
          </template>

          <!-- 金额相关 -->
          <template v-else-if="column.key === 'totalInAmount'">
            <span style="color: #52c41a; font-weight: 500;">¥{{ formatAmount(record.totalInAmount) }}</span>
          </template>

          <template v-else-if="column.key === 'totalOutAmount'">
            <span style="color: #ff4d4f; font-weight: 500;">¥{{ formatAmount(record.totalOutAmount) }}</span>
          </template>

          <template v-else-if="column.key === 'salesAmount'">
            <span style="color: #ff4d4f; font-weight: 500;">¥{{ formatAmount(record.salesAmount) }}</span>
          </template>

          <template v-else-if="column.key === 'returnAmount'">
            <span style="color: #fa8c16; font-weight: 500;">¥{{ formatAmount(record.returnAmount) }}</span>
          </template>

          <template v-else-if="column.key === 'netSalesAmount'">
            <span :style="{ color: getNetSalesColor(record.netSalesAmount), fontWeight: 500 }">
              ¥{{ formatAmount(record.netSalesAmount) }}
            </span>
          </template>
        </template>
      </a-table>
    </a-card>
  </div>
</template>

<script>
import { ref, shallowRef, reactive, onMounted } from 'vue'
import { message } from 'ant-design-vue'
import { SearchOutlined } from '@ant-design/icons-vue'
import { stockApi, categoryApi, productApi } from '@/api'
import dayjs from 'dayjs'
import SalesReturnBarChart from '@/components/SalesReturnBarChart.vue'

export default {
  name: 'MonthlySummary',
  components: {
    SearchOutlined,
    SalesReturnBarChart
  },
  setup() {
    const loading = ref(false)
    const summaryList = shallowRef([])  // 纯展示大列表：shallowRef 免深度响应式
    const categoryOptions = ref([])

    const searchForm = reactive({
      monthRange: [dayjs().subtract(23, 'month').format('YYYYMM'), dayjs().format('YYYYMM')],
      categoryId: undefined,
      productId: undefined
    })

    const productOptions = ref([])
    const productOptionsLoading = ref(false)

    const filterProductOption = (input, option) => {
      if (!input) return true
      return String(option.label || '').toLowerCase().includes(input.toLowerCase())
    }

    const loadProductOptions = async () => {
      productOptionsLoading.value = true
      try {
        const result = await productApi.querySimpleInfo({})
        productOptions.value = (result.data || []).map(p => ({
          value: p.id,
          label: p.name
        }))
      } catch (e) {
        console.error('加载商品下拉选项失败:', e)
      } finally {
        productOptionsLoading.value = false
      }
    }

    const columns = [
      {
        title: '月份',
        key: 'month',
        width: 100,
        align: 'center',
        fixed: 'left'
      },
      {
        title: '入库总数',
        key: 'totalInStock',
        width: 80,
        align: 'center'
      },
      {
        title: '入库明细',
        key: 'inStockDetail',
        width: 110,
        align: 'center'
      },
      {
        title: '出库总数',
        key: 'totalOutStock',
        width: 80,
        align: 'center'
      },
      {
        title: '出库明细',
        key: 'outStockDetail',
        width: 100,
        align: 'center'
      },
      {
        title: '净销售数',
        key: 'netSalesStock',
        width: 80,
        align: 'center'
      },
      {
        title: '退货率',
        key: 'returnRate',
        width: 80,
        align: 'center'
      },
      {
        title: '入库总额',
        key: 'totalInAmount',
        width: 120,
        align: 'right'
      },
      {
        title: '出库总额',
        key: 'totalOutAmount',
        width: 120,
        align: 'right'
      },
      {
        title: '销售金额',
        key: 'salesAmount',
        width: 120,
        align: 'right'
      },
      {
        title: '退货金额',
        key: 'returnAmount',
        width: 120,
        align: 'right'
      },
      {
        title: '净销售额',
        key: 'netSalesAmount',
        width: 120,
        align: 'right'
      }
    ]

    const loadCategories = async () => {
      try {
        const result = await categoryApi.getTree()
        categoryOptions.value = result.data || []
      } catch (error) {
        console.error('加载分类失败:', error)
      }
    }

    const loadMonthlySummary = async () => {
      loading.value = true
      try {
        const params = {}

        // 处理月份范围
        if (searchForm.monthRange && searchForm.monthRange.length === 2) {
          params.startMonth = searchForm.monthRange[0]
          params.endMonth = searchForm.monthRange[1]
        }

        // 处理分类ID
        if (searchForm.categoryId) {
          params.categoryId = searchForm.categoryId
        }

        if (searchForm.productId) {
          params.productId = searchForm.productId
        }

        const result = await stockApi.getMonthlySummary(params)
        summaryList.value = result.data || []
      } catch (error) {
        console.error('加载月度汇总失败:', error)
        message.error('加载月度汇总失败')
      } finally {
        loading.value = false
      }
    }

    const handleSearch = () => {
      loadMonthlySummary()
    }

    const handleReset = () => {
      Object.assign(searchForm, {
        monthRange: [dayjs().subtract(23, 'month').format('YYYYMM'), dayjs().format('YYYYMM')],
        categoryId: undefined,
        productId: undefined
      })
      loadMonthlySummary()
    }

    const disabledDate = (current) => {
      // 不能选择未来的月份
      return current && current > dayjs().endOf('month')
    }

    const formatAmount = (amount) => {
      if (amount === null || amount === undefined) {
        return '0.00'
      }

      const numAmount = Number(amount)
      if (isNaN(numAmount)) {
        return '0.00'
      }

      return numAmount.toFixed(2)
    }

    const getReturnRateColor = (returnRate) => {
      if (!returnRate || returnRate === '0%') return 'green'
      if (returnRate === '∞') return 'red'

      const rate = parseFloat(returnRate.replace('%', ''))
      if (rate <= 5) return 'green'
      if (rate <= 15) return 'orange'
      return 'red'
    }

    const getNetSalesColor = (value) => {
      const num = Number(value) || 0
      if (num > 0) return '#52c41a'
      if (num === 0) return '#666'
      return '#ff4d4f'
    }

    const formatMonth = (m) => {
      if (!m) return ''
      const s = String(m)
      if (s.length === 6) return `${s.slice(0, 4)}-${s.slice(4, 6)}`
      return s
    }

    onMounted(async () => {
      await Promise.all([loadCategories(), loadProductOptions()])
      await loadMonthlySummary()
    })

    return {
      loading,
      summaryList,
      categoryOptions,
      searchForm,
      columns,
      productOptions,
      productOptionsLoading,
      filterProductOption,
      handleSearch,
      handleReset,
      disabledDate,
      formatAmount,
      formatMonth,
      getReturnRateColor,
      getNetSalesColor
    }
  }
}
</script>

<style scoped>
.monthly-summary {
  padding: 0;
}

.page-header {
  margin-bottom: 24px;
}

.page-header h2 {
  margin: 0;
}

.filter-card {
  margin-bottom: 16px;
}

.chart-card {
  margin-bottom: 16px;
}

.search-form .ant-form-item {
  margin-bottom: 0;
}
</style>
